import { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { infaqService, DonationCampaign, CreateDonationDto, DonationResponse } from '../../services/infaqService';
import LoadingSpinner from '../../components/LoadingSpinner';
import { PhoneInput } from '../../components/ui';

function CampaignDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [campaign, setCampaign] = useState<(DonationCampaign & { daysLeft: number; donorCount: number }) | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [donationSuccess, setDonationSuccess] = useState<DonationResponse | null>(null);
  const [selectedAmount, setSelectedAmount] = useState<string>('');
  const [customAmount, setCustomAmount] = useState<string>('');
  const [formData, setFormData] = useState({
    donorName: '',
    donorEmail: '',
    donorPhone: '',
    isAnonymous: false,
    prayerRequest: '',
  });

  useEffect(() => {
    window.scrollTo(0, 0);

    // Check localStorage for existing payment success state on component mount
    const storedSuccess = localStorage.getItem('infaq_payment_success');
    if (storedSuccess) {
      try {
        const successData = JSON.parse(storedSuccess);
        console.log('Payment success detected from localStorage:', successData);
        setDonationSuccess(successData as DonationResponse);
        // Clear stored state to prevent re-showing
        localStorage.removeItem('infaq_payment_success');
        return;
      } catch (e) {
        console.error('Error parsing stored success data:', e);
      }
    }

    // Check URL parameters for payment status
    const urlParams = new URLSearchParams(window.location.search);
    const paymentStatus = urlParams.get('payment');
    const paymentStatus2 = urlParams.get('payment_status');
    const redirectStatus = urlParams.get('status');

    // If payment was successful via URL parameter, show success modal
    if (paymentStatus === 'success' || paymentStatus2 === 'success' || redirectStatus === 'success') {
      console.log('Payment success detected via URL parameter:', { paymentStatus, paymentStatus2, redirectStatus });

      // Store success state to localStorage to persist across page refreshes
      localStorage.setItem('infaq_payment_success', JSON.stringify({
        donation: {
          referenceNo: 'From RediPay',
          amount: 0,
          redipayDetails: { payment_url: window.location.href }
        }
      }));

      // Clear URL parameters to prevent re-showing success modal
      window.history.replaceState(null, '', window.location.pathname);
    }

    if (slug) {
      fetchCampaign();
    }
  }, [slug, window.location.search]);

  const fetchCampaign = async () => {
    if (!slug) return;

    try {
      setLoading(true);
      setError(null);
      const data = await infaqService.getCampaignBySlug(slug);
      setCampaign(data);
    } catch (err: any) {
      setError(err.message || 'Gagal memuat kempen');
    } finally {
      setLoading(false);
    }
  };

  const handleAmountSelect = (amount: string) => {
    if (amount === 'Lain-lain') {
      setSelectedAmount('custom');
    } else {
      setSelectedAmount(amount);
      setCustomAmount('');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!campaign) return;

    const amount = selectedAmount === 'custom' ? parseFloat(customAmount) : parseFloat(selectedAmount.replace('RM', ''));

    if (!amount || amount <= 0) {
      alert('Sila masukkan jumlah yang sah');
      return;
    }

    const donationData: CreateDonationDto = {
      campaignId: campaign.id,
      donorName: formData.donorName,
      donorEmail: formData.donorEmail,
      donorPhone: formData.donorPhone,
      isAnonymous: formData.isAnonymous,
      amount,
      prayerRequest: formData.prayerRequest,
    };

    try {
      setSubmitting(true);
      const response = await infaqService.createDonation(donationData);
      setDonationSuccess(response);
      // Store donation in localStorage (separate key to avoid confusion)
      localStorage.setItem('last_infaq_donation', JSON.stringify({
        referenceNo: response.donation.referenceNo,
        paymentId: response.redipayDetails.payment_id,
        transactionId: response.donation.referenceNo,
        amount: donationData.amount
      }));
      console.log('Donation created:', response);
    } catch (err: any) {
      alert(err.message || 'Gagal membuat dermaan');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  if (error || !campaign) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Kempen Tidak Dijumpai</h1>
          <p className="text-gray-600 mb-4">{error || 'Kempen tidak wujud'}</p>
          <Link to="/infaq" className="text-[#0D7330] hover:underline font-medium">
            Kembali ke Senarai Kempen
          </Link>
        </div>
      </div>
    );
  }

  if (donationSuccess) {
    return (
      <div className="min-h-screen bg-gray-50 py-12">
        <div className="max-w-2xl mx-auto px-6">
          <div className="bg-white rounded-xl shadow-md p-8 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Terima Kasih!</h2>
            <p className="text-sm text-gray-600 mb-2">
              Dermaan anda telah berjaya direkodkan. Sila lengkapkan pembayaran melalui pautan di bawah.
            </p>
            <p className="text-sm text-gray-600 mb-2">No. Rujukan</p>
            <p className="text-2xl font-bold text-[#0D7330] mb-4">
              {donationSuccess.donation?.referenceNo || 'N/A'}
            </p>
            <p className="text-lg font-semibold text-gray-700 mb-6">
              Jumlah: {donationSuccess.donation?.amount ? infaqService.formatCurrency(donationSuccess.donation.amount) : 'N/A'}
            </p>
            {donationSuccess.redipayDetails?.payment_url && (
              <a
                href={donationSuccess.redipayDetails.payment_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block w-full py-3 bg-[#0D7330] text-white font-semibold rounded-lg hover:bg-[#0a5c26] transition-colors mb-3"
              >
                Bayar Sekarang
              </a>
            )}
            <Link
              to="/infaq"
              className="inline-block w-full py-3 border-2 border-[#0D7330] text-[#0D7330] font-semibold rounded-lg hover:bg-[#0D7330] hover:text-white transition-colors"
            >
              Kembali ke Senarai Kempen
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="relative h-96 overflow-hidden">
        <img
          src={campaign.featuredImage || campaign.imageUrl || '/demo-help.jpg'}
          alt={campaign.title}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
        <div className="absolute bottom-0 left-0 right-0 p-8 md:p-16">
          <div className="max-w-7xl mx-auto">
            <Link
              to="/infaq"
              className="inline-flex items-center gap-2 text-white hover:text-gray-200 transition-colors mb-6"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Kembali ke Senarai Kempen
            </Link>

            <div className="flex items-center gap-2 mb-4">
              <span className="px-3 py-1 bg-[#0D7330] text-white text-sm font-medium rounded-full">
                {campaign.status === 'ACTIVE' ? 'Aktif' : campaign.status}
              </span>
              {campaign.daysLeft !== null && (
                <span className="px-3 py-1 bg-white/90 text-gray-700 text-sm font-medium rounded-full">
                  {campaign.daysLeft} hari lagi
                </span>
              )}
            </div>
            <h1 className="text-4xl md:text-5xl font-[CormorantGaramond] font-bold text-white mb-4">
              {campaign.title}
            </h1>
            <p className="text-lg text-gray-100 max-w-3xl">{campaign.description}</p>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-8 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* Progress Section */}
            <div className="bg-white rounded-xl shadow-md p-6 md:p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Kemajuan Kempen</h2>

              <div className="grid grid-cols-3 gap-6 mb-6">
                <div className="text-center p-4 bg-[#FAF9F6] rounded-lg">
                  <p className="text-sm text-gray-600 mb-2">Dikumpul</p>
                  <p className="text-2xl font-bold text-[#0D7330]">
                    {infaqService.formatCurrency(campaign.currentAmount)}
                  </p>
                </div>
                <div className="text-center p-4 bg-[#FAF9F6] rounded-lg">
                  <p className="text-sm text-gray-600 mb-2">Sasaran</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {infaqService.formatCurrency(campaign.targetAmount)}
                  </p>
                </div>
                <div className="text-center p-4 bg-[#FAF9F6] rounded-lg">
                  <p className="text-sm text-gray-600 mb-2">Penderma</p>
                  <p className="text-2xl font-bold text-gray-900">{campaign.donorCount}</p>
                </div>
              </div>

              <div className="mb-4">
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-gray-600">Kemajuan</span>
                  <span className="font-semibold text-[#0D7330]">
                    {Math.round(infaqService.calculateProgress(campaign.currentAmount, campaign.targetAmount))}%
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-4">
                  <div
                    className="bg-[#0D7330] h-4 rounded-full transition-all duration-500"
                    style={{ width: `${infaqService.calculateProgress(campaign.currentAmount, campaign.targetAmount)}%` }}
                  />
                </div>
              </div>

              <div className="flex items-center justify-between text-sm text-gray-600 pt-4 border-t border-gray-200">
                <span>Tarikh Mula: {new Date(campaign.startDate).toLocaleDateString('ms-MY')}</span>
                <span>
                  Tarikh Tamat:{' '}
                  {campaign.endDate ? new Date(campaign.endDate).toLocaleDateString('ms-MY') : 'Tiada'}
                </span>
              </div>
            </div>

            {/* Telus Penggunaan Dana Section */}
            <div className="bg-white rounded-xl shadow-md p-6 md:p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Telus Penggunaan Dana</h2>
              <p className="text-gray-600 mb-6">
                Setiap sen dermaan anda digunakan dengan telus. Berikut adalah pecahan penggunaan dana untuk
                kempen ini.
              </p>

              <div className="grid grid-cols-2 gap-4 mb-6">
                <div className="p-4 bg-[#0D7330]/10 rounded-lg border border-[#0D7330]/20">
                  <p className="text-sm text-gray-600 mb-1">Jumlah Dikutip</p>
                  <p className="text-xl font-bold text-[#0D7330]">
                    {infaqService.formatCurrency(campaign.currentAmount)}
                  </p>
                </div>
                <div className="p-4 bg-[#16592E]/10 rounded-lg border border-[#16592E]/20">
                  <p className="text-sm text-gray-600 mb-1">Digunakan</p>
                  <p className="text-xl font-bold text-[#16592E]">
                    {infaqService.formatCurrency(campaign.utilizedAmount)}
                  </p>
                </div>
              </div>

              {campaign.fundAllocations && campaign.fundAllocations.length > 0 && (
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Sejarah Penggunaan Dana</h3>
                  {campaign.fundAllocations.map((allocation: any, index: number) => (
                    <div key={index} className="border border-gray-200 rounded-lg p-4 hover:border-[#0D7330] transition-colors">
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <span className="inline-block px-2 py-1 bg-[#0D7330]/10 text-[#0D7330] text-xs font-medium rounded mb-2">
                            {allocation.category}
                          </span>
                          <h4 className="font-semibold text-gray-900">{allocation.description}</h4>
                        </div>
                        <p className="text-lg font-bold text-[#0D7330]">
                          {infaqService.formatCurrency(allocation.amount)}
                        </p>
                      </div>
                      <div className="flex items-center justify-between text-sm text-gray-500">
                        <span>{new Date(allocation.utilizationDate).toLocaleDateString('ms-MY')}</span>
                        <div className="flex items-center gap-2">
                          <svg className="w-4 h-4 text-[#0D7330]" fill="currentColor" viewBox="0 0 20 20">
                            <path
                              fillRule="evenodd"
                              d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                              clipRule="evenodd"
                            />
                          </svg>
                          <span>Diluluskan oleh {allocation.approvedBy}</span>
                        </div>
                      </div>
                      {allocation.receipts && allocation.receipts.length > 0 && (
                        <div className="mt-3 flex items-center gap-2 text-sm text-[#0D7330]">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13"
                            />
                          </svg>
                          <span>{allocation.receipts.length} resit dilampirkan</span>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {(!campaign.fundAllocations || campaign.fundAllocations.length === 0) && (
                <div className="text-center py-8 text-gray-500">
                  <svg className="w-12 h-12 mx-auto mb-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <p>Tidak ada penggunaan dana lagi untuk kempen ini.</p>
                </div>
              )}
            </div>

            {/* About Campaign Section */}
            <div className="bg-white rounded-xl shadow-md p-6 md:p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Mengenai Kempen Ini</h2>
              <div className="prose prose-gray max-w-none">
                <p className="text-gray-700 leading-relaxed mb-4">
                  {campaign.description}
                </p>
                <p className="text-gray-700 leading-relaxed">
                  Dermaan anda akan membantu kami menyediakan perkhidmatan yang lebih baik untuk komuniti.
                  Kami komited untuk memastikan setiap sumbangan digunakan dengan bertanggungjawab dan telus.
                </p>
              </div>
            </div>

            {/* Recent Donors Section */}
            {campaign.donations && campaign.donations.length > 0 && (
              <div className="bg-white rounded-xl shadow-md p-6 md:p-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Penderma Terkini</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {campaign.donations.slice(0, 6).map((donation: any, index: number) => (
                    <div key={index} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-[#0D7330] rounded-full flex items-center justify-center">
                          <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                          </svg>
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">
                            {donation.isAnonymous ? 'Penderma Tanpa Nama' : donation.donorName}
                          </p>
                          <p className="text-sm text-gray-500">
                            {new Date(donation.createdAt).toLocaleDateString('ms-MY')}
                          </p>
                        </div>
                      </div>
                      <p className="font-semibold text-[#0D7330]">
                        {infaqService.formatCurrency(donation.amount)}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Sidebar - Infaq Sekarang */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl shadow-md p-6 md:p-8 sticky top-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Infaq Sekarang</h2>

              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Amount Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    Pilih Jumlah Derma
                  </label>
                  <div className="grid grid-cols-3 gap-2 mb-3">
                    {['RM50', 'RM100', 'RM200', 'RM500', 'RM1000', 'Lain-lain'].map((amount) => (
                      <button
                        key={amount}
                        type="button"
                        onClick={() => handleAmountSelect(amount)}
                        className={`px-3 py-2 border-2 rounded-lg text-sm font-medium transition-colors ${
                          selectedAmount === amount
                            ? 'border-[#0D7330] bg-[#0D7330] text-white'
                            : 'border-gray-200 text-gray-700 hover:border-[#0D7330] hover:text-[#0D7330]'
                        }`}
                      >
                        {amount}
                      </button>
                    ))}
                  </div>
                  {selectedAmount === 'custom' && (
                    <input
                      type="number"
                      value={customAmount}
                      onChange={(e) => setCustomAmount(e.target.value)}
                      placeholder="Masukkan jumlah lain (RM)"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0D7330] focus:border-transparent"
                    />
                  )}
                </div>

                {/* Donor Information */}
                <div className="space-y-4">
                  <h3 className="text-sm font-medium text-gray-700">Maklumat Penderma</h3>

                  <div>
                    <label className="block text-sm text-gray-600 mb-1">Nama Penuh</label>
                    <input
                      type="text"
                      value={formData.donorName}
                      onChange={(e) => setFormData({ ...formData, donorName: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0D7330] focus:border-transparent"
                      required={!formData.isAnonymous}
                      disabled={formData.isAnonymous}
                    />
                  </div>

                  <div>
                    <label className="block text-sm text-gray-600 mb-1">E-mel</label>
                    <input
                      type="email"
                      value={formData.donorEmail}
                      onChange={(e) => setFormData({ ...formData, donorEmail: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0D7330] focus:border-transparent"
                      required={!formData.isAnonymous}
                      disabled={formData.isAnonymous}
                    />
                  </div>

                  <div>
                    <PhoneInput
                      label="No. Telefon"
                      value={formData.donorPhone}
                      onChange={(value) => setFormData({ ...formData, donorPhone: value })}
                      required={!formData.isAnonymous}
                      disabled={formData.isAnonymous}
                    />
                  </div>

                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="anonymous"
                      checked={formData.isAnonymous}
                      onChange={(e) => setFormData({ ...formData, isAnonymous: e.target.checked })}
                      className="w-4 h-4 text-[#0D7330] border-gray-300 rounded focus:ring-[#0D7330]"
                    />
                    <label htmlFor="anonymous" className="text-sm text-gray-600">
                      Sumbang secara tanpa nama
                    </label>
                  </div>
                </div>

                {/* Prayer Request */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Doa / Pesanan (Opsional)
                  </label>
                  <textarea
                    value={formData.prayerRequest}
                    onChange={(e) => setFormData({ ...formData, prayerRequest: e.target.value })}
                    rows="3"
                    placeholder="Tulis doa atau pesanan anda di sini..."
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0D7330] focus:border-transparent"
                  ></textarea>
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={submitting || !selectedAmount || (selectedAmount === 'custom' && !customAmount)}
                  className="w-full bg-[#0D7330] text-white py-4 rounded-lg font-semibold hover:bg-[#0a5c26] transition-colors flex items-center justify-center gap-2 disabled:bg-gray-400 disabled:cursor-not-allowed"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  {submitting ? 'Memproses...' : 'Sumbang Sekarang'}
                </button>

                {/* Payment Info */}
                <div className="flex items-center justify-center gap-2 text-sm text-gray-500">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                  <span>Pembayaran selamat melalui AiAhad</span>
                </div>
              </form>

              {/* Trust Indicators */}
              <div className="mt-6 pt-6 border-t border-gray-200 space-y-3">
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <svg className="w-4 h-4 text-[#0D7330]" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <span>Resit akan dihantar ke e-mel anda</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <svg className="w-4 h-4 text-[#0D7330]" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <span>Boleh dituntut untuk cukai pendapatan</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <svg className="w-4 h-4 text-[#0D7330]" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <span>Laporan penggunaan dana telus</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CampaignDetailPage;
